import React, { memo, useContext, useEffect, useState } from 'react';
import { Button, Form, FormControl } from 'react-bootstrap';

import { faSearch, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import CategoryFilter from './CategoryFilter';
import axios from 'axios';
import AuthContext from '../../authentication/AuthContext';
import UserContext from '../../user/UserContext';
import { useAlert } from 'react-alert';
import { IconWrapper } from '../IconWrapper';

export default memo(function ProductSearch(props) {
    const [searchTerm, setSearchTerm] = useState('');

    const [promoOnly, setPromoOnly] = useState(false);
    const [coopOwnLabel, setCoopOwnLabel] = useState(false);
    const [heritageOwnLabel, setHeritageOwnLabel] = useState(false);
    const [isOrganic, setIsOrganic] = useState(null);
    const [isGlutenFree, setIsGlutenFree] = useState(null);
    const [isVegetarian, setIsVegetarian] = useState(null);
    const [isFairtrade, setIsFairtrade] = useState(null);
    const [isHalal, setIsHalal] = useState(null);
    const [isVegan, setIsVegan] = useState(null);

    const [selectedSuperCategory, setSelectedSuperCategory] = useState('All Categories');
    const [selectedCategory, setSelectedCategory] = useState('');

    const cas = useContext(AuthContext);

    const { userDetails } = useContext(UserContext);

    const alert = useAlert();

    useEffect(() => {
        handleSubmit();
        // eslint-disable-next-line
    }, [promoOnly, coopOwnLabel, heritageOwnLabel, isOrganic, isGlutenFree, isVegetarian, isFairtrade, isHalal, isVegan, selectedSuperCategory, selectedCategory]);

    function clearSearch() {
        setSearchTerm('');
        setPromoOnly(false);
        setCoopOwnLabel(false);
        setHeritageOwnLabel(false);
        setIsOrganic(null);
        setIsGlutenFree(null);
        setIsVegetarian(null);
        setIsFairtrade(null);
        setIsHalal(null);
        setIsVegan(null);
        setSelectedCategory('');
        setSelectedSuperCategory('All Categories');
        props.setSearched(false);
    }

    function handleSubmit() {
        if (userDetails.currentFranchise) {
            if (
                searchTerm !== '' ||
                promoOnly ||
                coopOwnLabel ||
                heritageOwnLabel ||
                isOrganic ||
                isGlutenFree ||
                isVegetarian ||
                isFairtrade ||
                isHalal ||
                isVegan ||
                (selectedSuperCategory !== '' && selectedSuperCategory !== 'All Categories') ||
                selectedCategory !== ''
            ) {
                props.setLoading(true);
                // Let GTM know about form submission
                const dataLayer = window.dataLayer || [];
                dataLayer.push({
                    "event": "gtm.formSubmit",
                    "form": "productSearch"
                });

                let requestBody = {
                    maxResults: 3000,
                    query: searchTerm,
                    rangeStatus: ['ACTIVE', 'DISCONTINUED'],
                    categoryDescriptionFilter: selectedCategory,
                    superCategoryDescriptionFilter:
                        selectedSuperCategory !== 'All Categories' ? selectedSuperCategory : '',
                    costPromotions: promoOnly,
                    nisaOwnBrandFilter: heritageOwnLabel,
                    coopOwnBrandFilter: coopOwnLabel,
                    isOrganic: isOrganic,
                    isGlutenFree: isGlutenFree,
                    isVegetarian: isVegetarian,
                    isFairtrade: isFairtrade,
                    isHalal: isHalal,
                    isVegan: isVegan,
                    supplierCode: props.supplierCode
                };
                axios
                    .post(
                        window.CONFIG['ws.product-search-public.products.activocSearch'].replace(
                            '{franchiseCode}',
                            userDetails.currentFranchise.code
                        ),
                        requestBody,
                        {
                            headers: {
                                Authorization: cas,
                                'Content-Type': 'application/json'
                            }
                        }
                    )
                    .then(response => {
                        let json = response.data;
                        props.setSearched(true);
                        if (json.length > 0) {
                            props.setSearchResults(json);
                            props.setLoading(false);
                        } else {
                            props.setSearchResults([]);
                            props.setLoading(false);
                        }
                    })
                    .catch(() => {
                        props.setSearchResults([]);
                        props.setLoading(false);
                        alert.error('ERROR: Unable to retrieve search results.');
                    });
            } else {
                props.setSearched(false);
            }
        }
    }

    return (
        <div className={'product-search-container flex'}>
            <CategoryFilter
                supplierCode={props.supplierCode ? props.supplierCode : 'ALL'}
                categoryChanged={newCategory => {
                    setSelectedCategory(newCategory);
                    setSelectedSuperCategory('');
                }}
                superCategoryChanged={newSuperCategory => {
                    setSelectedSuperCategory(newSuperCategory);
                    setSelectedCategory('');
                }}
                selectedSuperCategory={selectedSuperCategory}
                selectedCategory={selectedCategory}
                upIcon={props.upIcon}
                downIcon={props.downIcon}
            />
            <div
                className={
                    'product-search-controls flex' + (props.showCount ? ' product-range-search-controls' : '')
                }
            >
                <FormControl
                    id={'product-search-input'}
                    type={'search'}
                    onChange={event => setSearchTerm(event.target.value)}
                    value={searchTerm}
                    placeholder={'code / description / brand / barcode'}
                    onKeyPress={event => {
                        if (event.key === 'Enter') {
                            handleSubmit();
                        }
                    }}
                    aria-label={'Search Term'}
                />
                <Button
                    id={'product-search-btn'}
                    variant={'primary'}
                    onClick={() => handleSubmit()}
                    className={'search-submit'}
                    title={'Search'}
                >
                    <IconWrapper icon={faSearch} />
                    Search
                </Button>
                {props.searched ? (
                    <Button
                        variant={'secondary'}
                        onClick={clearSearch}
                        className={'search-clear'}
                        title={'Clear Search'}
                    >
                        <IconWrapper icon={faSyncAlt} />
                        Clear Search
                    </Button>
                ) : null}
                {!props.hideFilters && (
                    <>
                        <div className={'line-breaker-div'} />
                        <div className={'product-search-filters flex'}>
                            <Form.Group>
                                <Form.Control
                                    id={'promo-only'}
                                    type={'checkbox'}
                                    onChange={event => {
                                        setPromoOnly(event.target.checked);
                                    }}
                                    checked={promoOnly}
                                />
                                <Form.Label htmlFor={'promo-only'} className={'promo-only-label'}>
                                    Promo Only
                                </Form.Label>
                            </Form.Group>
                            {userDetails.currentFranchise && !userDetails.currentFranchise.coopOBR && (
                                <Form.Group>
                                    <Form.Control
                                        id={'coop-own-label'}
                                        type={'checkbox'}
                                        onChange={event => {
                                            setCoopOwnLabel(event.target.checked);
                                        }}
                                        checked={coopOwnLabel}
                                    />
                                    <Form.Label htmlFor={'coop-own-label'}>Coop Own Label</Form.Label>
                                </Form.Group>
                            )}
                            {userDetails.currentFranchise && !userDetails.currentFranchise.nisaOBR && (
                                <Form.Group>
                                    <Form.Control
                                        id={'heritage-own-label'}
                                        type={'checkbox'}
                                        onChange={event => {
                                            setHeritageOwnLabel(event.target.checked);
                                        }}
                                        checked={heritageOwnLabel}
                                    />
                                    <Form.Label htmlFor={'heritage-own-label'}>Heritage Own Label</Form.Label>
                                </Form.Group>
                            )}
                            <Form.Group>
                                <Form.Control
                                    id={'organic'}
                                    type={'checkbox'}
                                    onChange={event => {
                                        setIsOrganic(event.target.checked ? true : null);
                                    }}
                                    checked={isOrganic}
                                />
                                <Form.Label htmlFor={'organic'} className={'organic-label'}>
                                    Organic
                                </Form.Label>
                            </Form.Group>
                            <Form.Group>
                                <Form.Control
                                    id={'gluten-free'}
                                    type={'checkbox'}
                                    onChange={event => {
                                        setIsGlutenFree(event.target.checked ? true : null);
                                    }}
                                    checked={isGlutenFree}
                                />
                                <Form.Label htmlFor={'gluten-free'} className={'gluten-free-label'}>
                                    Gluten Free
                                </Form.Label>
                            </Form.Group>
                            <Form.Group>
                                <Form.Control
                                    id={'vegetarian'}
                                    type={'checkbox'}
                                    onChange={event => {
                                        setIsVegetarian(event.target.checked ? true : null);
                                    }}
                                    checked={isVegetarian}
                                />
                                <Form.Label htmlFor={'vegetarian'} className={'vegetarian-label'}>
                                    Vegetarian
                                </Form.Label>
                            </Form.Group>
                            <Form.Group>
                                <Form.Control
                                    id={'fairtrade'}
                                    type={'checkbox'}
                                    onChange={event => {
                                        setIsFairtrade(event.target.checked ? true : null);
                                    }}
                                    checked={isFairtrade}
                                />
                                <Form.Label htmlFor={'fairtrade'} className={'fairtrade-label'}>
                                    Fairtrade
                                </Form.Label>
                            </Form.Group>
                            <Form.Group>
                                <Form.Control
                                    id={'halal'}
                                    type={'checkbox'}
                                    onChange={event => {
                                        setIsHalal(event.target.checked ? true : null);
                                    }}
                                    checked={isHalal}
                                />
                                <Form.Label htmlFor={'halal'} className={'halal-label'}>
                                    Halal
                                </Form.Label>
                            </Form.Group>
                            <Form.Group>
                                <Form.Control
                                    id={'vegan'}
                                    type={'checkbox'}
                                    onChange={event => {
                                        setIsVegan(event.target.checked ? true : null);
                                    }}
                                    checked={isVegan}
                                />
                                <Form.Label htmlFor={'vegan'} className={'vegan-label'}>
                                    Vegan
                                </Form.Label>
                            </Form.Group>
                        </div>
                    </>
                )}
            </div>
            <div className={'results-returned-container'}>
                {props.showCount && (
                    <div className={'results-returned'}>
                        {props.resultCount} Product{props.resultCount !== 1 && 's'}
                    </div>
                )}
            </div>
        </div>
    );
});
